import type {
  AuthTokens,
  FiscalParameterItem,
  FiscalParameterList,
  FiscalParameterVersionInput,
  ZbeZoneInput,
  ZbeZoneItem,
} from "./types";

const API_ORIGIN = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const ACCESS_KEY = "cochegest_platform_access";
const REFRESH_KEY = "cochegest_platform_refresh";
let accessTokenMemory: string | null = null;

export class PlatformApiError extends Error {
  constructor(message: string, public status: number, public code: string) {
    super(message);
  }
}

export function storeTokens(tokens: Pick<AuthTokens, "accessToken">): void {
  accessTokenMemory = tokens.accessToken;
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export function clearTokens(): void {
  accessTokenMemory = null;
  sessionStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function accessToken(): string | null {
  return accessTokenMemory;
}

async function refresh(): Promise<boolean> {
  const response = await fetch(`${API_ORIGIN}/api/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Cochegest-Client": "browser" },
    body: "{}",
  });
  if (!response.ok) {
    clearTokens();
    return false;
  }
  storeTokens(await response.json() as AuthTokens);
  return true;
}

async function apiError(response: Response): Promise<PlatformApiError> {
  const body = await response.json().catch(() => null) as { error?: { message?: string; code?: string } } | null;
  return new PlatformApiError(body?.error?.message ?? "No se ha podido completar la operación.", response.status, body?.error?.code ?? "HTTP_ERROR");
}

export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  const token = accessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(`${API_ORIGIN}/api/v1${path}`, { ...init, headers, credentials: "include" });
  if (response.status === 401 && retry && !path.startsWith("/auth/")) {
    if (await refresh()) return api<T>(path, init, false);
  }
  if (!response.ok) throw await apiError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function platformLogin(email: string, password: string, mfaCode?: string): Promise<AuthTokens> {
  return api<AuthTokens>("/auth/login", { method: "POST", headers: { "X-Cochegest-Client": "browser" }, body: JSON.stringify({ email, password, mfaCode: mfaCode || undefined }) });
}

export async function restoreSession(): Promise<boolean> {
  if (accessToken()) return true;
  return refresh();
}

export function getPlatformFiscalParameters(): Promise<FiscalParameterList> {
  return api<FiscalParameterList>("/platform-admin/fiscal-parameters");
}

export function putPlatformFiscalParameter(
  key: string,
  input: FiscalParameterVersionInput,
): Promise<FiscalParameterItem> {
  return api<FiscalParameterItem>(`/platform-admin/fiscal-parameters/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function getPlatformZbeZones(): Promise<{ items: ZbeZoneItem[] }> {
  return api<{ items: ZbeZoneItem[] }>("/platform-admin/zbe-zones");
}

export function putPlatformZbeZone(code: string, input: ZbeZoneInput): Promise<ZbeZoneItem> {
  return api<ZbeZoneItem>(`/platform-admin/zbe-zones/${encodeURIComponent(code)}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}
